import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import {
  ArrowRightLeft,
  BarChart3,
  Calendar,
  CheckSquare,
  Plus,
  Settings2,
  Trash2,
  Upload,
  User,
  Wallet,
} from 'lucide-react'
import { accountingApi } from '../api/accounting'
import { usersApi } from '../api/users'
import { Button } from '../components/common/Button'
import { getAttachmentFileUrl } from '../api/attachments'
import { useAuthStore } from '../store/authStore'
import type {
  AccountingFilters,
  AccountingSummary,
  Expense,
  ExpenseSettlement,
  ExpenseType,
  User as AppUser,
} from '../types'

type TabKey = 'overview' | 'add' | 'list' | 'claims' | 'settlements' | 'settings'
type Feedback = { tone: 'success' | 'error'; message: string } | null

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
})

function todayInputValue() {
  return new Date().toISOString().slice(0, 10)
}

function formatCurrency(amount: number): string {
  return currencyFormatter.format(Number.isFinite(amount) ? amount : 0)
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'Not set'
  return format(new Date(value), 'dd MMM yyyy, h:mm a')
}

function formatDateOnly(value?: string | null): string {
  if (!value) return 'Not set'
  return format(new Date(value), 'dd MMM yyyy')
}

function formatMonthLabel(value: string): string {
  const [year, month] = value.split('-').map(Number)
  return format(new Date(year, month - 1, 1), 'MMM yyyy')
}

function cleanFilters(filters: Record<string, string>): AccountingFilters {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value.trim() !== '')
  ) as AccountingFilters
}

function getBarWidth(amount: number, maxAmount: number): string {
  if (maxAmount <= 0) return '0%'
  return `${Math.max((amount / maxAmount) * 100, 6)}%`
}

function getBalanceTone(balance: number) {
  if (balance > 0) return { bg: '#fff7ed', color: '#c2410c', label: 'Pending' }
  if (balance < 0) return { bg: '#eff6ff', color: '#1d4ed8', label: 'Overpaid' }
  return { bg: '#ecfdf3', color: '#166534', label: 'Settled' }
}

function formatExpensePaymentStatus(status: Expense['paymentStatus']) {
  if (status === 'PAID') return 'Paid'
  if (status === 'PARTIALLY_PAID') return 'Partially Paid'
  return 'Unpaid'
}

function getExpensePaidDate(expense: Expense): string | null {
  const paidDates = (expense.settlementItems || [])
    .map((item) => item.settlement?.settledAt)
    .filter((value): value is string => Boolean(value))

  if (paidDates.length === 0) return null
  return paidDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
}

function expenseStatusTagStyle(status: Expense['paymentStatus']): React.CSSProperties {
  if (status === 'PAID') return tagStyle('#ecfdf3', '#166534')
  if (status === 'PARTIALLY_PAID') return tagStyle('#fff7ed', '#c2410c')
  return tagStyle('#fef2f2', '#b91c1c')
}

const initialExpenseFilters = {
  expenseTypeId: '',
  month: '',
  startDate: '',
  endDate: '',
}

const initialAdminFilters = {
  userId: '',
  expenseTypeId: '',
  month: '',
  startDate: '',
  endDate: '',
}

export default function AccountingPage() {
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
  const isPersonalAccountingView = !isAdmin

  const [tab, setTab] = useState<TabKey>(isAdmin ? 'claims' : 'overview')
  const [feedback, setFeedback] = useState<Feedback>(null)
  const [expenseFilters, setExpenseFilters] = useState(initialExpenseFilters)
  const [adminFilters, setAdminFilters] = useState(initialAdminFilters)
  const [expenseForm, setExpenseForm] = useState({
    expenseTypeId: '',
    amount: '',
    notes: '',
    noReceipt: false,
    receipt: null as File | null,
  })
  const [settlementForm, setSettlementForm] = useState({
    userId: '',
    amount: '',
    notes: '',
    settledAt: todayInputValue(),
    periodStart: '',
    periodEnd: '',
  })
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([])
  const [expenseTypeForm, setExpenseTypeForm] = useState({
    id: '',
    name: '',
    description: '',
    isActive: true,
  })

  const myExpenseFilters = useMemo(() => cleanFilters(expenseFilters), [expenseFilters])
  const adminQueryFilters = useMemo(() => cleanFilters(adminFilters), [adminFilters])

  const { data: expenseTypesData, isLoading: expenseTypesLoading } = useQuery({
    queryKey: ['accounting', 'expense-types'],
    queryFn: () => accountingApi.listExpenseTypes(),
  })

  const { data: myExpensesData, isLoading: myExpensesLoading } = useQuery({
    queryKey: ['accounting', 'my-expenses', myExpenseFilters],
    queryFn: () => accountingApi.listExpenses(myExpenseFilters),
  })

  const { data: mySummaryData, isLoading: mySummaryLoading } = useQuery({
    queryKey: ['accounting', 'my-summary', myExpenseFilters],
    queryFn: () => accountingApi.getSummary(myExpenseFilters),
  })

  const { data: adminSummaryData, isLoading: adminSummaryLoading } = useQuery({
    queryKey: ['accounting', 'summary', adminQueryFilters],
    queryFn: () => accountingApi.getSummary(adminQueryFilters),
    enabled: isAdmin,
  })

  const { data: adminExpensesData, isLoading: adminExpensesLoading } = useQuery({
    queryKey: ['accounting', 'expenses', adminQueryFilters],
    queryFn: () => accountingApi.listExpenses(adminQueryFilters),
    enabled: isAdmin,
  })

  const { data: settlementsData, isLoading: settlementsLoading } = useQuery({
    queryKey: ['accounting', 'settlements', adminQueryFilters],
    queryFn: () => accountingApi.listSettlements(adminQueryFilters),
    enabled: isAdmin,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers(),
    enabled: isAdmin,
  })

  const { data: settlementCandidatesData, isLoading: settlementCandidatesLoading } = useQuery({
    queryKey: ['accounting', 'settlement-candidates', settlementForm.userId],
    queryFn: () => accountingApi.getSettlementCandidates(settlementForm.userId),
    enabled: isAdmin && !!settlementForm.userId,
  })

  const expenseTypes = expenseTypesData?.data?.data || []
  const myExpenses = myExpensesData?.data?.data || []
  const mySummary = mySummaryData?.data?.data
  const adminSummary = adminSummaryData?.data?.data
  const adminExpenses = adminExpensesData?.data?.data || []
  const settlements = settlementsData?.data?.data || []
  const users = usersData?.data?.data || []
  const accountingUsers = useMemo(
    () => users.filter((member) => member.isActive && member.canSubmitExpenses),
    [users]
  )
  const settlementCandidates = settlementCandidatesData?.data?.data

  const myStats = useMemo(() => {
    const totalAmount = myExpenses.reduce((sum, expense) => sum + expense.amount, 0)
    const noReceiptCount = myExpenses.filter((expense) => expense.noReceipt).length
    return {
      totalAmount,
      noReceiptCount,
      claimCount: myExpenses.length,
    }
  }, [myExpenses])

  const selectedUserBalance = useMemo(() => {
    if (!settlementForm.userId || !adminSummary) return null
    return adminSummary.userBalances.find((entry) => entry.user.id === settlementForm.userId) || null
  }, [adminSummary, settlementForm.userId])

  const selectedSettlementExpenses = useMemo(
    () => settlementCandidates?.expenses.filter((expense) => selectedExpenseIds.includes(expense.id)) || [],
    [selectedExpenseIds, settlementCandidates]
  )

  const selectedSettlementTotal = useMemo(
    () => selectedSettlementExpenses.reduce((sum, expense) => sum + expense.outstandingAmount, 0),
    [selectedSettlementExpenses]
  )

  const settlementAmountValue = Number(settlementForm.amount || 0)
  const projectedOverpayment = Math.max(0, settlementAmountValue - selectedSettlementTotal)
  const projectedUnderpayment = Math.max(0, selectedSettlementTotal - settlementAmountValue)

  const maxTypeTotal = useMemo(() => {
    if (!adminSummary?.typeBreakdown.length) return 0
    return Math.max(...adminSummary.typeBreakdown.map((entry) => entry.totalAmount))
  }, [adminSummary])

  const maxMonthlyTotal = useMemo(() => {
    const expenseMax = Math.max(...(adminSummary?.monthlyExpenseTotals.map((item) => item.totalAmount) || [0]))
    const settlementMax = Math.max(...(adminSummary?.monthlySettlementTotals.map((item) => item.totalAmount) || [0]))
    return Math.max(expenseMax, settlementMax)
  }, [adminSummary])

  useEffect(() => {
    if (isAdmin) return
    if (tab === 'claims' || tab === 'settlements' || tab === 'settings') {
      setTab('overview')
    }
  }, [isAdmin, tab])

  const invalidateAccounting = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['accounting'] }),
      qc.invalidateQueries({ queryKey: ['users'] }),
    ])
  }

  const createExpenseMutation = useMutation({
    mutationFn: () =>
      accountingApi.createExpense({
        expenseTypeId: expenseForm.expenseTypeId,
        amount: expenseForm.amount,
        notes: expenseForm.notes,
        noReceipt: expenseForm.noReceipt,
        receipt: expenseForm.receipt,
      }),
    onSuccess: async () => {
      await invalidateAccounting()
      setExpenseForm({
        expenseTypeId: '',
        amount: '',
        notes: '',
        noReceipt: false,
        receipt: null,
      })
      setFeedback({ tone: 'success', message: 'Expense claim submitted successfully.' })
      setTab('claims')
    },
    onError: (error: any) => {
      setFeedback({ tone: 'error', message: error?.response?.data?.error || 'Could not submit the expense.' })
    },
  })

  const deleteExpenseMutation = useMutation({
    mutationFn: (id: string) => accountingApi.deleteExpense(id),
    onSuccess: async () => {
      await invalidateAccounting()
      setFeedback({ tone: 'success', message: 'Expense claim deleted successfully.' })
    },
    onError: (error: any) => {
      setFeedback({ tone: 'error', message: error?.response?.data?.error || 'Could not delete the expense claim.' })
    },
  })

  const createSettlementMutation = useMutation({
    mutationFn: () =>
      accountingApi.createSettlement({
        userId: settlementForm.userId,
        amount: settlementForm.amount,
        expenseIds: selectedExpenseIds,
        notes: settlementForm.notes,
        settledAt: settlementForm.settledAt ? new Date(`${settlementForm.settledAt}T12:00:00`).toISOString() : undefined,
        periodStart: settlementForm.periodStart || undefined,
        periodEnd: settlementForm.periodEnd || undefined,
      }),
    onSuccess: async () => {
      await invalidateAccounting()
      setSettlementForm({
        userId: '',
        amount: '',
        notes: '',
        settledAt: todayInputValue(),
        periodStart: '',
        periodEnd: '',
      })
      setSelectedExpenseIds([])
      setFeedback({ tone: 'success', message: 'Settlement recorded successfully.' })
      setTab('settlements')
    },
    onError: (error: any) => {
      setFeedback({ tone: 'error', message: error?.response?.data?.error || 'Could not record the settlement.' })
    },
  })

  const saveExpenseTypeMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: expenseTypeForm.name,
        description: expenseTypeForm.description,
        isActive: expenseTypeForm.isActive,
      }
      return expenseTypeForm.id
        ? accountingApi.updateExpenseType(expenseTypeForm.id, payload)
        : accountingApi.createExpenseType(payload)
    },
    onSuccess: async () => {
      await invalidateAccounting()
      setExpenseTypeForm({ id: '', name: '', description: '', isActive: true })
      setFeedback({ tone: 'success', message: 'Expense type saved successfully.' })
      setTab('settings')
    },
    onError: (error: any) => {
      setFeedback({ tone: 'error', message: error?.response?.data?.error || 'Could not save the expense type.' })
    },
  })

  const deleteExpenseTypeMutation = useMutation({
    mutationFn: (id: string) => accountingApi.deleteExpenseType(id),
    onSuccess: async () => {
      await invalidateAccounting()
      setFeedback({ tone: 'success', message: 'Expense type deleted successfully.' })
      if (expenseTypeForm.id) {
        setExpenseTypeForm({ id: '', name: '', description: '', isActive: true })
      }
    },
    onError: (error: any) => {
      setFeedback({ tone: 'error', message: error?.response?.data?.error || 'Could not delete the expense type.' })
    },
  })

  const handleExpenseSubmit = () => {
    setFeedback(null)
    if (!expenseForm.expenseTypeId) {
      setFeedback({ tone: 'error', message: 'Please select an expense type.' })
      return
    }
    if (!expenseForm.amount || Number(expenseForm.amount) <= 0) {
      setFeedback({ tone: 'error', message: 'Please enter a valid expense amount.' })
      return
    }
    if (!expenseForm.noReceipt && !expenseForm.receipt) {
      setFeedback({ tone: 'error', message: 'Please attach a receipt or mark this claim as no receipt.' })
      return
    }
    createExpenseMutation.mutate()
  }

  const handleSettlementSubmit = () => {
    setFeedback(null)
    if (!settlementForm.userId) {
      setFeedback({ tone: 'error', message: 'Please select a user for the payment.' })
      return
    }
    if (selectedExpenseIds.length === 0) {
      setFeedback({ tone: 'error', message: 'Please select at least one unpaid bill for this payout.' })
      return
    }
    if (!settlementForm.amount || Number(settlementForm.amount) <= 0) {
      setFeedback({ tone: 'error', message: 'Please enter a valid payment amount.' })
      return
    }
    createSettlementMutation.mutate()
  }

  const canShowAdminTabs = isAdmin
  const activeExpenseTypes = expenseTypes.filter((expenseType) => expenseType.isActive)

  return (
    <div style={pageStyle}>
      <div style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>Accounting</div>
          <h1 style={titleStyle}>Expenses, settlements, and balance tracking</h1>
          <p style={subtitleStyle}>
            Submit claims with receipts, review pending reimbursements, settle staff balances by period, and understand monthly cost patterns without leaving the PM system.
          </p>
        </div>
        <div style={heroBadgeStyle}>
          <Wallet size={18} />
          <span>{isAdmin ? 'Admin review enabled' : 'Personal claims workspace'}</span>
        </div>
      </div>

      {feedback && (
        <div style={{ ...feedbackStyle, ...(feedback.tone === 'error' ? feedbackErrorStyle : feedbackSuccessStyle) }}>
          {feedback.message}
        </div>
      )}

      <div style={tabBarStyle}>
        {isPersonalAccountingView ? (
          <>
            <TabButton active={tab === 'overview'} icon={<BarChart3 size={15} />} label="Overview" onClick={() => setTab('overview')} />
            <TabButton active={tab === 'add'} icon={<Plus size={15} />} label="Add Expense" onClick={() => setTab('add')} />
            <TabButton active={tab === 'list'} icon={<Wallet size={15} />} label="Claim List" onClick={() => setTab('list')} />
          </>
        ) : (
          <TabButton active={tab === 'claims'} icon={<Wallet size={15} />} label="My Claims" onClick={() => setTab('claims')} />
        )}
        {canShowAdminTabs && (
          <TabButton active={tab === 'overview'} icon={<BarChart3 size={15} />} label="Overview" onClick={() => setTab('overview')} />
        )}
        {canShowAdminTabs && (
          <TabButton active={tab === 'settlements'} icon={<ArrowRightLeft size={15} />} label="Settlements" onClick={() => setTab('settlements')} />
        )}
        {isAdmin && (
          <TabButton active={tab === 'settings'} icon={<Settings2 size={15} />} label="Settings" onClick={() => setTab('settings')} />
        )}
      </div>

      {tab === 'overview' && isPersonalAccountingView && (
        <>
          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>My Overview</h2>
                <div style={panelSubtitleStyle}>See your total claims, total paid, pending amount, and overpaid balance in one clear view.</div>
              </div>
              <span style={tagStyle('#eef2ff', '#4338ca')}>
                {mySummaryLoading ? 'Refreshing...' : 'Personal balance view'}
              </span>
            </div>

            <FilterRow
              title="My filters"
              filters={expenseFilters}
              onChange={setExpenseFilters}
              expenseTypes={activeExpenseTypes}
            />

            <div style={overviewStatsGridStyle}>
              <SummaryCard label="Total Expenses" value={formatCurrency(mySummary?.totals.totalExpenses || 0)} tone="#2563eb" />
              <SummaryCard label="Total Paid" value={formatCurrency(mySummary?.totals.totalPaid || 0)} tone="#0f766e" />
              <SummaryCard label="Pending" value={formatCurrency(mySummary?.totals.pendingAmount || 0)} tone="#c2410c" />
              <SummaryCard label="Overpaid" value={formatCurrency(mySummary?.totals.overpaidAmount || 0)} tone="#7c3aed" />
            </div>
          </section>

          <div style={stackedSectionStyle}>
            <section style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <h2 style={panelTitleStyle}>My Balance</h2>
                  <div style={panelSubtitleStyle}>Positive balance means payment is still pending. Negative means you have extra paid credit.</div>
                </div>
              </div>
              <UserBalanceTable summary={mySummary} />
            </section>

            <section style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <h2 style={panelTitleStyle}>My Claim Status</h2>
                  <div style={panelSubtitleStyle}>Quick view of your claimed bills and whether each one is paid or still pending.</div>
                </div>
              </div>
              <ExpenseTable expenses={myExpenses} loading={myExpensesLoading} />
            </section>
          </div>
        </>
      )}

      {tab === 'add' && isPersonalAccountingView && (
        <section style={panelStyle}>
          <div style={panelHeaderStyle}>
            <div>
              <h2 style={panelTitleStyle}>Add New Expense</h2>
              <div style={panelSubtitleStyle}>Receipt is mandatory unless you explicitly mark the claim as no receipt.</div>
            </div>
            <span style={tagStyle('#eff6ff', '#1d4ed8')}>
              Auto captures date, time, and submitted by
            </span>
          </div>

          <div style={formGridStyle}>
            <label style={fieldLabelStyle}>
              Expense Type *
              <select
                value={expenseForm.expenseTypeId}
                onChange={(event) => setExpenseForm((current) => ({ ...current, expenseTypeId: event.target.value }))}
                style={fieldInputStyle}
                disabled={expenseTypesLoading}
              >
                <option value="">Please select</option>
                {activeExpenseTypes.map((expenseType) => (
                  <option key={expenseType.id} value={expenseType.id}>{expenseType.name}</option>
                ))}
              </select>
            </label>

            <label style={fieldLabelStyle}>
              Amount *
              <input
                type="number"
                min="0"
                step="0.01"
                value={expenseForm.amount}
                onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value }))}
                style={fieldInputStyle}
                placeholder="0.00"
              />
            </label>

            <label style={{ ...fieldLabelStyle, gridColumn: '1 / -1' }}>
              Description / Notes
              <textarea
                value={expenseForm.notes}
                onChange={(event) => setExpenseForm((current) => ({ ...current, notes: event.target.value }))}
                style={{ ...fieldInputStyle, minHeight: 110, paddingTop: 10, resize: 'vertical' }}
                placeholder="Add a note about the expense, why it was made, or any extra context for approval."
              />
            </label>

            <div style={{ ...fieldLabelStyle, gridColumn: '1 / -1', gap: 10 }}>
              <span>Receipt *</span>
              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={expenseForm.noReceipt}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      noReceipt: event.target.checked,
                      receipt: event.target.checked ? null : current.receipt,
                    }))
                  }
                />
                No receipt for this expense
              </label>
              <label style={uploadBoxStyle(expenseForm.noReceipt)}>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.pdf"
                  disabled={expenseForm.noReceipt}
                  style={{ display: 'none' }}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      receipt: event.target.files?.[0] || null,
                    }))
                  }
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={uploadIconStyle}><Upload size={16} /></div>
                  <div>
                    <div style={uploadTitleStyle}>
                      {expenseForm.noReceipt
                        ? 'Receipt upload not required'
                        : expenseForm.receipt
                          ? expenseForm.receipt.name
                          : 'Upload receipt image or PDF'}
                    </div>
                    <div style={uploadSubtitleStyle}>
                      {expenseForm.noReceipt
                        ? 'This claim will be marked as submitted without a receipt.'
                        : 'Allowed types: PNG, JPG, WEBP, PDF'}
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div style={buttonRowStyle}>
            <Button
              variant="secondary"
              onClick={() => setExpenseForm({ expenseTypeId: '', amount: '', notes: '', noReceipt: false, receipt: null })}
            >
              Clear
            </Button>
            <Button variant="primary" loading={createExpenseMutation.isPending} onClick={handleExpenseSubmit}>
              <Plus size={14} /> Submit Expense
            </Button>
          </div>
        </section>
      )}

      {tab === 'list' && isPersonalAccountingView && (
        <section style={panelStyle}>
          <div style={panelHeaderStyle}>
            <div>
              <h2 style={panelTitleStyle}>My Claim List</h2>
              <div style={panelSubtitleStyle}>View your claimed date, paid date, current status, and delete only claims that have not been paid yet.</div>
            </div>
            <span style={tagStyle('#f8fafc', '#475569')}>
              {myExpensesLoading ? 'Loading...' : `${myStats.claimCount} claims`}
            </span>
          </div>

          <div style={statsGridStyle}>
            <SummaryCard label="Claimed" value={formatCurrency(myStats.totalAmount)} tone="#2563eb" />
            <SummaryCard label="Claims" value={String(myStats.claimCount)} tone="#0f766e" />
            <SummaryCard label="No Receipt" value={String(myStats.noReceiptCount)} tone="#c2410c" />
          </div>

          <FilterRow
            title="My claim filters"
            filters={expenseFilters}
            onChange={setExpenseFilters}
            expenseTypes={activeExpenseTypes}
          />

          <ExpenseTable
            expenses={myExpenses}
            loading={myExpensesLoading}
            deletingExpenseId={deleteExpenseMutation.variables}
            onDeleteExpense={(expense) => {
              if (!window.confirm(`Delete this ${formatCurrency(expense.amount)} expense claim?`)) return
              setFeedback(null)
              deleteExpenseMutation.mutate(expense.id)
            }}
          />
        </section>
      )}

      {tab === 'claims' && (
        <>
          <div style={claimsGridStyle}>
            <section style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <h2 style={panelTitleStyle}>Add New Expense</h2>
                  <div style={panelSubtitleStyle}>Receipt is mandatory unless you explicitly mark the claim as no receipt.</div>
                </div>
                <span style={tagStyle('#eff6ff', '#1d4ed8')}>
                  Auto captures date, time, and submitted by
                </span>
              </div>

              <div style={formGridStyle}>
                <label style={fieldLabelStyle}>
                  Expense Type *
                  <select
                    value={expenseForm.expenseTypeId}
                    onChange={(event) => setExpenseForm((current) => ({ ...current, expenseTypeId: event.target.value }))}
                    style={fieldInputStyle}
                    disabled={expenseTypesLoading}
                  >
                    <option value="">Please select</option>
                    {activeExpenseTypes.map((expenseType) => (
                      <option key={expenseType.id} value={expenseType.id}>{expenseType.name}</option>
                    ))}
                  </select>
                </label>

                <label style={fieldLabelStyle}>
                  Amount *
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value }))}
                    style={fieldInputStyle}
                    placeholder="0.00"
                  />
                </label>

                <label style={{ ...fieldLabelStyle, gridColumn: '1 / -1' }}>
                  Description / Notes
                  <textarea
                    value={expenseForm.notes}
                    onChange={(event) => setExpenseForm((current) => ({ ...current, notes: event.target.value }))}
                    style={{ ...fieldInputStyle, minHeight: 110, paddingTop: 10, resize: 'vertical' }}
                    placeholder="Add a note about the expense, why it was made, or any extra context for approval."
                  />
                </label>

                <div style={{ ...fieldLabelStyle, gridColumn: '1 / -1', gap: 10 }}>
                  <span>Receipt *</span>
                  <label style={checkboxRowStyle}>
                    <input
                      type="checkbox"
                      checked={expenseForm.noReceipt}
                      onChange={(event) =>
                        setExpenseForm((current) => ({
                          ...current,
                          noReceipt: event.target.checked,
                          receipt: event.target.checked ? null : current.receipt,
                        }))
                      }
                    />
                    No receipt for this expense
                  </label>
                  <label style={uploadBoxStyle(expenseForm.noReceipt)}>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,.pdf"
                      disabled={expenseForm.noReceipt}
                      style={{ display: 'none' }}
                      onChange={(event) =>
                        setExpenseForm((current) => ({
                          ...current,
                          receipt: event.target.files?.[0] || null,
                        }))
                      }
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={uploadIconStyle}><Upload size={16} /></div>
                      <div>
                        <div style={uploadTitleStyle}>
                          {expenseForm.noReceipt
                            ? 'Receipt upload not required'
                            : expenseForm.receipt
                              ? expenseForm.receipt.name
                              : 'Upload receipt image or PDF'}
                        </div>
                        <div style={uploadSubtitleStyle}>
                          {expenseForm.noReceipt
                            ? 'This claim will be marked as submitted without a receipt.'
                            : 'Allowed types: PNG, JPG, WEBP, PDF'}
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div style={buttonRowStyle}>
                <Button
                  variant="secondary"
                  onClick={() => setExpenseForm({ expenseTypeId: '', amount: '', notes: '', noReceipt: false, receipt: null })}
                >
                  Clear
                </Button>
                <Button variant="primary" loading={createExpenseMutation.isPending} onClick={handleExpenseSubmit}>
                  <Plus size={14} /> Submit Expense
                </Button>
              </div>
            </section>

            <section style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <h2 style={panelTitleStyle}>My Claim Snapshot</h2>
                  <div style={panelSubtitleStyle}>Quick view of what you have submitted in the current filter. Accidental claims can be deleted before they are covered by settlement.</div>
                </div>
                <span style={tagStyle('#f8fafc', '#475569')}>
                  {myExpensesLoading ? 'Loading...' : `${myStats.claimCount} claims`}
                </span>
              </div>

              <div style={statsGridStyle}>
                <SummaryCard label="Claimed" value={formatCurrency(myStats.totalAmount)} tone="#2563eb" />
                <SummaryCard label="Claims" value={String(myStats.claimCount)} tone="#0f766e" />
                <SummaryCard label="No Receipt" value={String(myStats.noReceiptCount)} tone="#c2410c" />
              </div>

              <FilterRow
                title="My expense filters"
                filters={expenseFilters}
                onChange={setExpenseFilters}
                expenseTypes={activeExpenseTypes}
              />

              <ExpenseTable
                expenses={myExpenses}
                loading={myExpensesLoading}
                deletingExpenseId={deleteExpenseMutation.variables}
                onDeleteExpense={(expense) => {
                  if (!window.confirm(`Delete this ${formatCurrency(expense.amount)} expense claim?`)) return
                  setFeedback(null)
                  deleteExpenseMutation.mutate(expense.id)
                }}
              />
            </section>
          </div>
        </>
      )}

      {tab === 'overview' && canShowAdminTabs && (
        <>
          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Accounting Dashboard</h2>
                <div style={panelSubtitleStyle}>Filter by user, expense type, month, or a custom date range.</div>
              </div>
              <span style={tagStyle('#eef2ff', '#4338ca')}>
                {adminSummaryLoading ? 'Refreshing...' : 'Live balance view'}
              </span>
            </div>

            <FilterRow
              title="Admin filters"
              filters={adminFilters}
              onChange={setAdminFilters}
              expenseTypes={expenseTypes}
              users={accountingUsers}
              showUser
            />

            <div style={overviewStatsGridStyle}>
              <SummaryCard label="Total Expenses" value={formatCurrency(adminSummary?.totals.totalExpenses || 0)} tone="#2563eb" />
              <SummaryCard label="Total Paid" value={formatCurrency(adminSummary?.totals.totalPaid || 0)} tone="#0f766e" />
              <SummaryCard label="Pending" value={formatCurrency(adminSummary?.totals.pendingAmount || 0)} tone="#c2410c" />
              <SummaryCard label="Overpaid" value={formatCurrency(adminSummary?.totals.overpaidAmount || 0)} tone="#7c3aed" />
            </div>
          </section>

          <div style={overviewGridStyle}>
            <section style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <h2 style={panelTitleStyle}>Expense Report by Type</h2>
                  <div style={panelSubtitleStyle}>Monthly cost concentration by expense type.</div>
                </div>
              </div>
              <TypeBreakdownChart summary={adminSummary} maxAmount={maxTypeTotal} />
            </section>

            <section style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div>
                  <h2 style={panelTitleStyle}>Monthly Expense vs Paid</h2>
                  <div style={panelSubtitleStyle}>Compare submitted claims against settlements in the selected period.</div>
                </div>
              </div>
              <MonthlyTotalsChart summary={adminSummary} maxAmount={maxMonthlyTotal} />
            </section>
          </div>

          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Pending Amount by User</h2>
                <div style={panelSubtitleStyle}>Positive balance means pending payment. Negative means overpayment.</div>
              </div>
              <span style={tagStyle('#fff7ed', '#9a3412')}>
                {adminSummary?.userBalances.length || 0} users in view
              </span>
            </div>
            <UserBalanceTable summary={adminSummary} />
          </section>

          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Filtered Expenses</h2>
                <div style={panelSubtitleStyle}>Use this list to review expense claims by user, type, month, or date range.</div>
              </div>
            </div>
            <ExpenseTable expenses={adminExpenses} loading={adminExpensesLoading} />
          </section>
        </>
      )}

      {tab === 'settlements' && canShowAdminTabs && (
        <div style={claimsGridStyle}>
          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Record Settlement</h2>
                <div style={panelSubtitleStyle}>Choose the unpaid bills for this payout, then record the payment against those selected claims.</div>
              </div>
              <span style={tagStyle('#eff6ff', '#1d4ed8')}>
                Supports overpayment and underpayment
              </span>
            </div>

            <div style={formGridStyle}>
              <label style={fieldLabelStyle}>
                User *
                <select
                  value={settlementForm.userId}
                  onChange={(event) => {
                    const nextUserId = event.target.value
                    setSettlementForm((current) => ({ ...current, userId: nextUserId, amount: '' }))
                    setSelectedExpenseIds([])
                  }}
                  style={fieldInputStyle}
                >
                  <option value="">Please select</option>
                  {accountingUsers.map((member) => (
                    <option key={member.id} value={member.id}>{member.fullName}</option>
                  ))}
                </select>
              </label>

              <label style={fieldLabelStyle}>
                Payment Amount *
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settlementForm.amount}
                  onChange={(event) => setSettlementForm((current) => ({ ...current, amount: event.target.value }))}
                  style={fieldInputStyle}
                  placeholder="0.00"
                />
              </label>

              <label style={fieldLabelStyle}>
                Payment Date
                <input
                  type="date"
                  value={settlementForm.settledAt}
                  onChange={(event) => setSettlementForm((current) => ({ ...current, settledAt: event.target.value }))}
                  style={fieldInputStyle}
                />
              </label>

              <label style={fieldLabelStyle}>
                Period Start
                <input
                  type="date"
                  value={settlementForm.periodStart}
                  onChange={(event) => setSettlementForm((current) => ({ ...current, periodStart: event.target.value }))}
                  style={fieldInputStyle}
                />
              </label>

              <label style={fieldLabelStyle}>
                Period End
                <input
                  type="date"
                  value={settlementForm.periodEnd}
                  onChange={(event) => setSettlementForm((current) => ({ ...current, periodEnd: event.target.value }))}
                  style={fieldInputStyle}
                />
              </label>

              <label style={{ ...fieldLabelStyle, gridColumn: '1 / -1' }}>
                Notes
                <textarea
                  value={settlementForm.notes}
                  onChange={(event) => setSettlementForm((current) => ({ ...current, notes: event.target.value }))}
                  style={{ ...fieldInputStyle, minHeight: 96, paddingTop: 10, resize: 'vertical' }}
                  placeholder="Optional notes for this payout run or period."
                />
              </label>
            </div>

            {settlementForm.userId && (
              <div style={highlightCardStyle}>
                <div style={highlightTitleStyle}>Selected payout summary</div>
                {settlementCandidatesLoading ? (
                  <div style={highlightMetaStyle}>Loading unpaid bills...</div>
                ) : settlementCandidates ? (
                  <>
                    <div style={summaryStripStyle}>
                      <MiniSummary label="Unpaid bills" value={String(settlementCandidates.expenses.length)} />
                      <MiniSummary label="Outstanding" value={formatCurrency(settlementCandidates.outstandingTotal)} />
                      <MiniSummary label="Selected total" value={formatCurrency(selectedSettlementTotal)} />
                      <MiniSummary label="Credit" value={formatCurrency(settlementCandidates.availableCredit)} />
                    </div>
                    <div style={highlightMetaStyle}>
                      Recommended payment after available credit: {formatCurrency(settlementCandidates.recommendedPaymentAmount)}
                    </div>
                    <div style={{ ...highlightMetaStyle, color: projectedOverpayment > 0 ? '#1d4ed8' : '#475569' }}>
                      {projectedOverpayment > 0
                        ? `Extra payment after the selected bills: ${formatCurrency(projectedOverpayment)}`
                        : projectedUnderpayment > 0 && settlementAmountValue > 0
                          ? `Still pending after this payout: ${formatCurrency(projectedUnderpayment)}`
                          : 'Select the unpaid bills to include in this payment.'}
                    </div>
                  </>
                ) : (
                  <div style={highlightMetaStyle}>Choose an Accounting user to load unpaid bills.</div>
                )}
              </div>
            )}

            {settlementForm.userId && (
              <div style={billPickerStyle}>
                <div style={billPickerHeaderStyle}>
                  <div>
                    <div style={panelTitleStyle}>Unclaimed Bills</div>
                    <div style={panelSubtitleStyle}>Select all claims or manually choose the bills to settle now.</div>
                  </div>
                  {settlementCandidates && settlementCandidates.expenses.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const allIds = settlementCandidates.expenses.map((expense) => expense.id)
                          const total = settlementCandidates.expenses.reduce((sum, expense) => sum + expense.outstandingAmount, 0)
                          setSelectedExpenseIds(allIds)
                          setSettlementForm((current) => ({ ...current, amount: String(total) }))
                        }}
                      >
                        <CheckSquare size={13} /> Select all claims
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedExpenseIds([])
                          setSettlementForm((current) => ({ ...current, amount: '' }))
                        }}
                      >
                        Clear selection
                      </Button>
                    </div>
                  )}
                </div>

                {settlementCandidatesLoading ? (
                  <div style={emptyStateStyle}>Loading unpaid bills...</div>
                ) : !settlementCandidates || settlementCandidates.expenses.length === 0 ? (
                  <div style={emptyStateStyle}>No unpaid bills found for this Accounting user.</div>
                ) : (
                  <div style={billListStyle}>
                    {settlementCandidates.expenses.map((expense) => {
                      const isSelected = selectedExpenseIds.includes(expense.id)
                      return (
                        <label key={expense.id} style={billItemStyle(isSelected)}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(event) => {
                              setSelectedExpenseIds((current) =>
                                event.target.checked
                                  ? [...current, expense.id]
                                  : current.filter((id) => id !== expense.id)
                              )
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={billItemHeaderStyle}>
                              <span style={billItemTypeStyle}>{expense.expenseType.name}</span>
                              <span style={billItemAmountStyle}>{formatCurrency(expense.outstandingAmount)}</span>
                            </div>
                            <div style={billItemMetaStyle}>
                              Submitted {formatDateTime(expense.submittedAt)} • Claimed {formatCurrency(expense.amount)} • Paid so far {formatCurrency(expense.paidAmount)}
                            </div>
                            {expense.notes && <div style={billItemNotesStyle}>{expense.notes}</div>}
                          </div>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {selectedUserBalance && (
              <div style={highlightCardStyle}>
                <div style={highlightTitleStyle}>Selected User Balance</div>
                <div style={highlightValueStyle}>{formatCurrency(selectedUserBalance.balance)}</div>
                <div style={highlightMetaStyle}>
                  Pending: {formatCurrency(selectedUserBalance.pendingAmount)} • Overpaid: {formatCurrency(selectedUserBalance.overpaidAmount)}
                </div>
              </div>
            )}

            <div style={buttonRowStyle}>
              <Button
                variant="secondary"
                onClick={() => {
                  setSettlementForm({ userId: '', amount: '', notes: '', settledAt: todayInputValue(), periodStart: '', periodEnd: '' })
                  setSelectedExpenseIds([])
                }}
              >
                Clear
              </Button>
              <Button variant="primary" loading={createSettlementMutation.isPending} onClick={handleSettlementSubmit}>
                <ArrowRightLeft size={14} /> Record Payment
              </Button>
            </div>
          </section>

          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Settlement History</h2>
                <div style={panelSubtitleStyle}>Track payouts by user, month, or date range, including which bills were covered and any extra amount.</div>
              </div>
            </div>

            <SettlementTable settlements={settlements} loading={settlementsLoading} />
          </section>
        </div>
      )}

      {tab === 'settings' && isAdmin && (
        <div style={claimsGridStyle}>
          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Expense Type Settings</h2>
                <div style={panelSubtitleStyle}>Super admins can add, edit, activate, or retire expense types.</div>
              </div>
            </div>

            <div style={formGridStyle}>
              <label style={fieldLabelStyle}>
                Expense Type Name *
                <input
                  value={expenseTypeForm.name}
                  onChange={(event) => setExpenseTypeForm((current) => ({ ...current, name: event.target.value }))}
                  style={fieldInputStyle}
                  placeholder="e.g. Travel, Meals, Office Supplies"
                />
              </label>

              <label style={fieldLabelStyle}>
                Status
                <select
                  value={expenseTypeForm.isActive ? 'active' : 'inactive'}
                  onChange={(event) => setExpenseTypeForm((current) => ({ ...current, isActive: event.target.value === 'active' }))}
                  style={fieldInputStyle}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              <label style={{ ...fieldLabelStyle, gridColumn: '1 / -1' }}>
                Description
                <textarea
                  value={expenseTypeForm.description}
                  onChange={(event) => setExpenseTypeForm((current) => ({ ...current, description: event.target.value }))}
                  style={{ ...fieldInputStyle, minHeight: 96, paddingTop: 10, resize: 'vertical' }}
                  placeholder="Optional guidance for users when they select this expense type."
                />
              </label>
            </div>

            <div style={buttonRowStyle}>
              <Button
                variant="secondary"
                onClick={() => setExpenseTypeForm({ id: '', name: '', description: '', isActive: true })}
              >
                {expenseTypeForm.id ? 'Cancel edit' : 'Clear'}
              </Button>
              <Button
                variant="primary"
                loading={saveExpenseTypeMutation.isPending}
                onClick={() => {
                  if (!expenseTypeForm.name.trim()) {
                    setFeedback({ tone: 'error', message: 'Please enter an expense type name.' })
                    return
                  }
                  saveExpenseTypeMutation.mutate()
                }}
              >
                <Plus size={14} /> {expenseTypeForm.id ? 'Save Changes' : 'Add Expense Type'}
              </Button>
            </div>
          </section>

          <section style={panelStyle}>
            <div style={panelHeaderStyle}>
              <div>
                <h2 style={panelTitleStyle}>Existing Expense Types</h2>
                <div style={panelSubtitleStyle}>Delete is allowed only when the type has never been used.</div>
              </div>
              <span style={tagStyle('#f8fafc', '#475569')}>
                {expenseTypes.length} total
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {expenseTypes.length === 0 ? (
                <div style={emptyStateStyle}>No expense types configured yet.</div>
              ) : (
                expenseTypes.map((expenseType) => (
                  <div key={expenseType.id} style={typeCardStyle}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={typeNameStyle}>{expenseType.name}</div>
                        <span style={tagStyle(expenseType.isActive ? '#ecfdf3' : '#f8fafc', expenseType.isActive ? '#166534' : '#64748b')}>
                          {expenseType.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span style={tagStyle('#eff6ff', '#1d4ed8')}>
                          {expenseType._count?.expenses || 0} claims
                        </span>
                      </div>
                      <div style={typeMetaStyle}>
                        {expenseType.description || 'No description added yet.'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          setExpenseTypeForm({
                            id: expenseType.id,
                            name: expenseType.name,
                            description: expenseType.description || '',
                            isActive: expenseType.isActive,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        loading={deleteExpenseTypeMutation.isPending && deleteExpenseTypeMutation.variables === expenseType.id}
                        onClick={() => deleteExpenseTypeMutation.mutate(expenseType.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

function TabButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button onClick={onClick} style={tabButtonStyle(active)}>
      {icon}
      {label}
    </button>
  )
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={{ ...summaryValueStyle, color: tone }}>{value}</div>
    </div>
  )
}

function MiniSummary({ label, value }: { label: string; value: string }) {
  return (
    <div style={miniSummaryStyle}>
      <div style={miniSummaryLabelStyle}>{label}</div>
      <div style={miniSummaryValueStyle}>{value}</div>
    </div>
  )
}

function FilterRow({
  title,
  filters,
  onChange,
  expenseTypes,
  users = [],
  showUser = false,
}: {
  title: string
  filters: { userId?: string; expenseTypeId: string; month: string; startDate: string; endDate: string }
  onChange: React.Dispatch<React.SetStateAction<any>>
  expenseTypes: ExpenseType[]
  users?: AppUser[]
  showUser?: boolean
}) {
  return (
    <div style={filterWrapStyle}>
      <div style={filterTitleStyle}>{title}</div>
      <div style={filterGridStyle}>
        {showUser && (
          <label style={fieldLabelStyle}>
            User
            <select
              value={filters.userId || ''}
              onChange={(event) => onChange((current: any) => ({ ...current, userId: event.target.value }))}
              style={fieldInputStyle}
            >
              <option value="">All users</option>
              {users.map((member) => (
                <option key={member.id} value={member.id}>{member.fullName}</option>
              ))}
            </select>
          </label>
        )}

        <label style={fieldLabelStyle}>
          Expense Type
          <select
            value={filters.expenseTypeId}
            onChange={(event) => onChange((current: any) => ({ ...current, expenseTypeId: event.target.value }))}
            style={fieldInputStyle}
          >
            <option value="">All expense types</option>
            {expenseTypes.map((expenseType) => (
              <option key={expenseType.id} value={expenseType.id}>{expenseType.name}</option>
            ))}
          </select>
        </label>

        <label style={fieldLabelStyle}>
          Month
          <input
            type="month"
            value={filters.month}
            onChange={(event) =>
              onChange((current: any) => ({
                ...current,
                month: event.target.value,
                startDate: event.target.value ? '' : current.startDate,
                endDate: event.target.value ? '' : current.endDate,
              }))
            }
            style={fieldInputStyle}
          />
        </label>

        <label style={fieldLabelStyle}>
          Start Date
          <input
            type="date"
            value={filters.startDate}
            onChange={(event) =>
              onChange((current: any) => ({
                ...current,
                month: event.target.value ? '' : current.month,
                startDate: event.target.value,
              }))
            }
            style={fieldInputStyle}
          />
        </label>

        <label style={fieldLabelStyle}>
          End Date
          <input
            type="date"
            value={filters.endDate}
            onChange={(event) =>
              onChange((current: any) => ({
                ...current,
                month: event.target.value ? '' : current.month,
                endDate: event.target.value,
              }))
            }
            style={fieldInputStyle}
          />
        </label>
      </div>
    </div>
  )
}

function ExpenseTable({
  expenses,
  loading,
  deletingExpenseId,
  onDeleteExpense,
}: {
  expenses: Expense[]
  loading: boolean
  deletingExpenseId?: string
  onDeleteExpense?: (expense: Expense) => void
}) {
  if (loading) return <div style={emptyStateStyle}>Loading expenses...</div>
  if (expenses.length === 0) return <div style={emptyStateStyle}>No expenses found for the selected filters.</div>

  return (
    <div style={tableWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Submitted</th>
            <th style={thStyle}>Paid Date</th>
            <th style={thStyle}>User</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Claimed</th>
            <th style={thStyle}>Paid</th>
            <th style={thStyle}>Outstanding</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Receipt</th>
            <th style={thStyle}>Notes</th>
            {onDeleteExpense && <th style={thStyle}>Action</th>}
          </tr>
        </thead>
        <tbody>
          {expenses.map((expense) => (
            <tr key={expense.id}>
              <td style={tdStyle}>{formatDateTime(expense.submittedAt)}</td>
              <td style={tdStyle}>{getExpensePaidDate(expense) ? formatDateTime(getExpensePaidDate(expense)) : 'Not paid yet'}</td>
              <td style={tdStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <User size={14} color="#64748b" />
                  <span>{expense.user.fullName}</span>
                </div>
              </td>
              <td style={tdStyle}>{expense.expenseType.name}</td>
              <td style={tdStyleStrong}>{formatCurrency(expense.amount)}</td>
              <td style={tdStyle}>{formatCurrency(expense.paidAmount)}</td>
              <td style={tdStyle}>{formatCurrency(expense.outstandingAmount)}</td>
              <td style={tdStyle}>
                <span style={expenseStatusTagStyle(expense.paymentStatus)}>{formatExpensePaymentStatus(expense.paymentStatus)}</span>
              </td>
              <td style={tdStyle}>
                {expense.receiptUrl ? (
                  <a href={getAttachmentFileUrl(expense.receiptUrl)} target="_blank" rel="noreferrer" style={linkStyle}>
                    {expense.receiptFilename || 'Open receipt'}
                  </a>
                ) : expense.noReceipt ? (
                  <span style={tagStyle('#fff7ed', '#c2410c')}>No receipt</span>
                ) : (
                  'Missing'
                )}
              </td>
              <td style={tdStyle}>{expense.notes || '—'}</td>
              {onDeleteExpense && (
                <td style={tdStyle}>
                  {expense.paymentStatus === 'UNPAID' ? (
                    <Button
                      variant="danger"
                      size="sm"
                      loading={deletingExpenseId === expense.id}
                      onClick={() => onDeleteExpense(expense)}
                    >
                      <Trash2 size={13} /> Delete
                    </Button>
                  ) : (
                    <span style={tagStyle('#f8fafc', '#64748b')}>Paid claim locked</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SettlementTable({ settlements, loading }: { settlements: ExpenseSettlement[]; loading: boolean }) {
  if (loading) return <div style={emptyStateStyle}>Loading settlements...</div>
  if (settlements.length === 0) return <div style={emptyStateStyle}>No settlements found for the selected filters.</div>

  return (
    <div style={tableWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Paid On</th>
            <th style={thStyle}>User</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Covered Bills</th>
            <th style={thStyle}>Extra</th>
            <th style={thStyle}>Period</th>
            <th style={thStyle}>Paid By</th>
            <th style={thStyle}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {settlements.map((settlement) => (
            <tr key={settlement.id}>
              <td style={tdStyle}>{formatDateTime(settlement.settledAt)}</td>
              <td style={tdStyle}>{settlement.user.fullName}</td>
              <td style={tdStyleStrong}>{formatCurrency(settlement.amount)}</td>
              <td style={tdStyle}>
                {settlement.items.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={tagStyle('#eff6ff', '#1d4ed8')}>
                      {settlement.items.length} bill{settlement.items.length === 1 ? '' : 's'}
                    </span>
                    {settlement.items.slice(0, 3).map((item) => (
                      <span key={item.id} style={settlementItemTextStyle}>
                        {item.expense?.expenseType.name || 'Expense'}: {formatCurrency(item.amount)}
                      </span>
                    ))}
                    {settlement.items.length > 3 && (
                      <span style={settlementItemTextStyle}>+{settlement.items.length - 3} more</span>
                    )}
                  </div>
                ) : (
                  <span style={tagStyle('#fff7ed', '#9a3412')}>No bill selected</span>
                )}
              </td>
              <td style={tdStyle}>
                {settlement.unallocatedAmount > 0 ? (
                  <span style={tagStyle('#eff6ff', '#1d4ed8')}>{formatCurrency(settlement.unallocatedAmount)}</span>
                ) : (
                  '—'
                )}
              </td>
              <td style={tdStyle}>
                {settlement.periodStart || settlement.periodEnd
                  ? `${settlement.periodStart ? formatDateOnly(settlement.periodStart) : 'Start open'} - ${settlement.periodEnd ? formatDateOnly(settlement.periodEnd) : 'End open'}`
                  : 'Not specified'}
              </td>
              <td style={tdStyle}>{settlement.paidBy.fullName}</td>
              <td style={tdStyle}>{settlement.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TypeBreakdownChart({ summary, maxAmount }: { summary?: AccountingSummary; maxAmount: number }) {
  if (!summary) return <div style={emptyStateStyle}>Loading summary...</div>
  if (summary.typeBreakdown.length === 0) return <div style={emptyStateStyle}>No expense totals available for this filter.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {summary.typeBreakdown.map((entry) => (
        <div key={entry.expenseTypeId} style={chartRowStyle}>
          <div style={chartLabelRowStyle}>
            <div>
              <div style={chartPrimaryLabelStyle}>{entry.expenseTypeName}</div>
              <div style={chartSecondaryLabelStyle}>{entry.expenseCount} claims</div>
            </div>
            <div style={chartAmountStyle}>{formatCurrency(entry.totalAmount)}</div>
          </div>
          <div style={chartTrackStyle}>
            <div style={{ ...chartFillStyle, width: getBarWidth(entry.totalAmount, maxAmount), background: '#2563eb' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function MonthlyTotalsChart({ summary, maxAmount }: { summary?: AccountingSummary; maxAmount: number }) {
  if (!summary) return <div style={emptyStateStyle}>Loading monthly totals...</div>
  if (summary.monthlyExpenseTotals.length === 0 && summary.monthlySettlementTotals.length === 0) {
    return <div style={emptyStateStyle}>No monthly totals available for this filter.</div>
  }

  const months = Array.from(new Set([
    ...summary.monthlyExpenseTotals.map((item) => item.month),
    ...summary.monthlySettlementTotals.map((item) => item.month),
  ])).sort()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {months.map((month) => {
        const expenseAmount = summary.monthlyExpenseTotals.find((item) => item.month === month)?.totalAmount || 0
        const settlementAmount = summary.monthlySettlementTotals.find((item) => item.month === month)?.totalAmount || 0
        return (
          <div key={month} style={chartRowStyle}>
            <div style={chartLabelRowStyle}>
              <div style={chartPrimaryLabelStyle}>{formatMonthLabel(month)}</div>
              <div style={chartSecondaryLabelStyle}>
                Expense {formatCurrency(expenseAmount)} • Paid {formatCurrency(settlementAmount)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={chartTrackStyle}>
                <div style={{ ...chartFillStyle, width: getBarWidth(expenseAmount, maxAmount), background: '#2563eb' }} />
              </div>
              <div style={chartTrackStyle}>
                <div style={{ ...chartFillStyle, width: getBarWidth(settlementAmount, maxAmount), background: '#16a34a' }} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function UserBalanceTable({ summary }: { summary?: AccountingSummary }) {
  if (!summary) return <div style={emptyStateStyle}>Loading user balances...</div>
  if (summary.userBalances.length === 0) return <div style={emptyStateStyle}>No user balances available for this filter.</div>

  return (
    <div style={tableWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>User</th>
            <th style={thStyle}>Claimed</th>
            <th style={thStyle}>Paid</th>
            <th style={thStyle}>Balance</th>
            <th style={thStyle}>Status</th>
          </tr>
        </thead>
        <tbody>
          {summary.userBalances.map((entry) => {
            const tone = getBalanceTone(entry.balance)
            return (
              <tr key={entry.user.id}>
                <td style={tdStyle}>{entry.user.fullName}</td>
                <td style={tdStyle}>{formatCurrency(entry.expenseTotal)}</td>
                <td style={tdStyle}>{formatCurrency(entry.paidTotal)}</td>
                <td style={tdStyleStrong}>{formatCurrency(entry.balance)}</td>
                <td style={tdStyle}>
                  <span style={tagStyle(tone.bg, tone.color)}>{tone.label}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '100%',
  boxSizing: 'border-box',
  padding: '24px',
  background: 'linear-gradient(180deg, #f7f8fc 0%, #eef4f7 100%)',
}

const heroStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 24,
  marginBottom: 18,
}

const eyebrowStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#0f766e',
}

const titleStyle: React.CSSProperties = {
  margin: '6px 0 10px',
  fontSize: 30,
  color: '#0f172a',
  lineHeight: 1.1,
}

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  maxWidth: 780,
  fontSize: 14,
  color: '#64748b',
  lineHeight: 1.6,
}

const heroBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 16px',
  borderRadius: 999,
  background: '#ffffff',
  color: '#1d4ed8',
  border: '1px solid #dbeafe',
  boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
  fontWeight: 700,
}

const feedbackStyle: React.CSSProperties = {
  marginBottom: 18,
  padding: '14px 16px',
  borderRadius: 14,
  fontSize: 14,
  fontWeight: 600,
}

const feedbackSuccessStyle: React.CSSProperties = {
  background: '#ecfdf3',
  color: '#166534',
  border: '1px solid #bbf7d0',
}

const feedbackErrorStyle: React.CSSProperties = {
  background: '#fef2f2',
  color: '#991b1b',
  border: '1px solid #fecaca',
}

const tabBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  marginBottom: 18,
}

const tabButtonStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  height: 40,
  padding: '0 14px',
  borderRadius: 999,
  border: active ? '1px solid #bfdbfe' : '1px solid #dbe3ee',
  background: active ? '#eff6ff' : '#ffffff',
  color: active ? '#1d4ed8' : '#475569',
  cursor: 'pointer',
  fontWeight: 700,
})

const panelStyle: React.CSSProperties = {
  borderRadius: 22,
  border: '1px solid #e2e8f0',
  background: 'rgba(255,255,255,0.96)',
  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
  padding: 20,
}

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 18,
}

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 20,
  color: '#0f172a',
}

const panelSubtitleStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12.5,
  color: '#64748b',
}

const claimsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(320px, 0.95fr) minmax(0, 1.25fr)',
  gap: 16,
}

const overviewGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 16,
  margin: '16px 0',
}

const stackedSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 16,
  margin: '16px 0',
}

const formGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14,
}

const filterWrapStyle: React.CSSProperties = {
  marginBottom: 18,
  padding: 16,
  borderRadius: 16,
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
}

const filterTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#64748b',
  marginBottom: 12,
}

const filterGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 12,
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  fontWeight: 700,
  color: '#475569',
}

const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 42,
  borderRadius: 12,
  border: '1px solid #dbe3ee',
  background: '#ffffff',
  padding: '0 12px',
  fontSize: 14,
  color: '#0f172a',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
}

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  color: '#334155',
}

const uploadBoxStyle = (disabled: boolean): React.CSSProperties => ({
  display: 'block',
  borderRadius: 16,
  border: `1px dashed ${disabled ? '#cbd5e1' : '#93c5fd'}`,
  background: disabled ? '#f8fafc' : '#eff6ff',
  padding: 16,
  cursor: disabled ? 'not-allowed' : 'pointer',
})

const uploadIconStyle: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#dbeafe',
  color: '#1d4ed8',
}

const uploadTitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#0f172a',
  fontWeight: 700,
}

const uploadSubtitleStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12.5,
  color: '#64748b',
}

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  marginTop: 18,
}

const statsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12,
  marginBottom: 18,
}

const overviewStatsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 12,
}

const summaryCardStyle: React.CSSProperties = {
  borderRadius: 18,
  border: '1px solid #e2e8f0',
  background: '#ffffff',
  padding: '16px 18px',
  minHeight: 94,
}

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#64748b',
}

const summaryValueStyle: React.CSSProperties = {
  marginTop: 10,
  fontSize: 26,
  fontWeight: 800,
  color: '#0f172a',
}

const tableWrapStyle: React.CSSProperties = {
  overflowX: 'auto',
  borderRadius: 14,
  border: '1px solid #e2e8f0',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: '#ffffff',
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 14px',
  fontSize: 12,
  fontWeight: 800,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
}

const tdStyle: React.CSSProperties = {
  padding: '14px',
  fontSize: 13.5,
  color: '#334155',
  borderBottom: '1px solid #eef2f7',
  verticalAlign: 'top',
}

const tdStyleStrong: React.CSSProperties = {
  ...tdStyle,
  fontWeight: 700,
  color: '#0f172a',
}

const emptyStateStyle: React.CSSProperties = {
  border: '1px dashed #cbd5e1',
  borderRadius: 14,
  padding: 22,
  textAlign: 'center',
  color: '#94a3b8',
  fontSize: 13,
}

const chartRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}

const chartLabelRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}

const chartPrimaryLabelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: '#0f172a',
}

const chartSecondaryLabelStyle: React.CSSProperties = {
  marginTop: 3,
  fontSize: 12,
  color: '#64748b',
}

const chartAmountStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 800,
  color: '#0f172a',
}

const chartTrackStyle: React.CSSProperties = {
  height: 12,
  borderRadius: 999,
  background: '#e2e8f0',
  overflow: 'hidden',
}

const chartFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: 999,
}

const highlightCardStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 16,
  borderRadius: 16,
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
}

const highlightTitleStyle: React.CSSProperties = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 800,
  color: '#1d4ed8',
}

const highlightValueStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 28,
  fontWeight: 800,
  color: '#0f172a',
}

const highlightMetaStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 13,
  color: '#475569',
}

const summaryStripStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 10,
  marginTop: 14,
}

const miniSummaryStyle: React.CSSProperties = {
  borderRadius: 14,
  border: '1px solid #bfdbfe',
  background: '#ffffff',
  padding: '12px 14px',
}

const miniSummaryLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#64748b',
}

const miniSummaryValueStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 18,
  fontWeight: 800,
  color: '#0f172a',
}

const billPickerStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 16,
  borderRadius: 16,
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
}

const billPickerHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 14,
}

const billListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}

const billItemStyle = (selected: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  borderRadius: 14,
  border: `1px solid ${selected ? '#93c5fd' : '#dbe3ee'}`,
  background: selected ? '#eff6ff' : '#ffffff',
  padding: 14,
  cursor: 'pointer',
})

const billItemHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}

const billItemTypeStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 800,
  color: '#0f172a',
}

const billItemAmountStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 800,
  color: '#1d4ed8',
}

const billItemMetaStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 12.5,
  color: '#64748b',
  lineHeight: 1.5,
}

const billItemNotesStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 13,
  color: '#334155',
}

const settlementItemTextStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: '#475569',
  lineHeight: 1.4,
}

const typeCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 14,
  padding: 16,
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  background: '#ffffff',
}

const typeNameStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 800,
  color: '#0f172a',
}

const typeMetaStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: '#64748b',
  lineHeight: 1.5,
}

const linkStyle: React.CSSProperties = {
  color: '#2563eb',
  fontWeight: 700,
  textDecoration: 'none',
}

const tagStyle = (background: string, color: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 28,
  padding: '0 10px',
  borderRadius: 999,
  background,
  color,
  fontSize: 12,
  fontWeight: 800,
})
